/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import React, { useRef, useState } from "react";

// Intentionally limited to plain URLs and [label](https://...) links.
// The forum stores plain text, so links are rendered as React elements rather
// than interpolated HTML. Only HTTP(S) URLs may become clickable.
const LINK_PATTERN =
  /\[([^\]\n]{1,200})\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<>"']+)/gi;

function safeHttpUrl(value) {
  try {
    const url = new URL(value);
    return ["http:", "https:"].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

export function ForumTopicLinks({ text }) {
  const content = String(text || "");
  const parts = [];
  const pattern = new RegExp(LINK_PATTERN.source, LINK_PATTERN.flags);
  let cursor = 0;
  let match;

  while ((match = pattern.exec(content)) !== null) {
    const index = match.index;
    if (index > cursor) parts.push(content.slice(cursor, index));

    const isNamedLink = Boolean(match[1]);
    const rawUrl = isNamedLink ? match[2] : match[3];
    const trimmedUrl = isNamedLink
      ? rawUrl
      : rawUrl.replace(/[.,;:!?\])}]+$/, "");
    const href = safeHttpUrl(trimmedUrl);

    if (href) {
      parts.push(
        <a
          key={`${index}_${href}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer nofollow ugc"
          onClick={(event) => event.stopPropagation()}
          className="font-medium text-[#01767A] underline decoration-[#24C3BE]/60 underline-offset-[3px] hover:text-[#374049] hover:decoration-[#374049] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#24C3BE]"
        >
          {isNamedLink ? match[1] : trimmedUrl}
        </a>,
      );
      if (!isNamedLink && trimmedUrl.length < rawUrl.length) {
        parts.push(rawUrl.slice(trimmedUrl.length));
      }
    } else {
      parts.push(match[0]);
    }
    cursor = index + match[0].length;
  }

  if (cursor < content.length) parts.push(content.slice(cursor));
  return <>{parts}</>;
}

export function ForumLinkInsert({
  textareaRef,
  text,
  onChange,
  maxLength,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const selectionRef = useRef({ start: 0, end: 0 });

  function openEditor() {
    const field = textareaRef.current;
    const start = field?.selectionStart ?? text.length;
    const end = field?.selectionEnd ?? start;
    const selection = text.slice(start, end);
    selectionRef.current = { start, end };
    setLabel(selection.startsWith("http") ? "" : selection);
    setUrl(selection.startsWith("http") ? selection : "");
    setError("");
    setOpen(true);
  }

  function insertLink(event) {
    event.preventDefault();
    const raw = url.trim();
    if (!raw) {
      setError("Enter the link address.");
      return;
    }
    const normalized = /^[a-z][a-z\d+.-]*:/i.test(raw) ? raw : `https://${raw}`;
    const href = safeHttpUrl(normalized);
    if (!href) {
      setError("Enter a valid http:// or https:// address.");
      return;
    }

    const { start, end } = selectionRef.current;
    const displayLabel = label.trim().replace(/[[\]\r\n]/g, "") || href;
    const markup = `[${displayLabel}](${href})`;
    const nextText = text.slice(0, start) + markup + text.slice(end);
    if (nextText.length > maxLength) {
      setError(
        `The message is limited to ${maxLength.toLocaleString()} characters.`,
      );
      return;
    }

    onChange(nextText);
    setOpen(false);
    setError("");
    requestAnimationFrame(() => {
      const field = textareaRef.current;
      field?.focus();
      field?.setSelectionRange(start + markup.length, start + markup.length);
    });
  }

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={openEditor}
        disabled={disabled}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-lg border border-[#93CA87]/50 bg-white px-3 py-1.5 text-xs font-semibold text-[#01767A] transition hover:border-[#24C3BE] hover:bg-[#24C3BE]/10 disabled:opacity-50"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4"
          aria-hidden="true"
        >
          <path d="M10 13.5a5 5 0 0 0 7.1 0l2.1-2.1a5 5 0 0 0-7.1-7.1L10 6.4" />
          <path d="M14 10.5a5 5 0 0 0-7.1 0l-2.1 2.1a5 5 0 0 0 7.1 7.1l2.1-2.1" />
        </svg>
        Insert link
      </button>
      {open && (
        <form
          onSubmit={insertLink}
          className="mt-2 max-w-lg space-y-2 rounded-xl border border-[#93CA87]/50 bg-[#FDFBF8] p-3"
        >
          <label className="block text-xs font-semibold text-[#374049]">
            Link text
            <input
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="Google Play"
              maxLength={200}
              autoFocus
              className="mt-1 block w-full rounded-lg border border-[#93CA87]/50 bg-white px-3 py-2 text-sm font-normal outline-none focus:border-[#24C3BE]"
            />
          </label>
          <label className="block text-xs font-semibold text-[#374049]">
            Web address
            <input
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="https://play.google.com/..."
              inputMode="url"
              type="text"
              required
              className="mt-1 block w-full rounded-lg border border-[#93CA87]/50 bg-white px-3 py-2 text-sm font-normal outline-none focus:border-[#24C3BE]"
            />
          </label>
          {error && (
            <p role="alert" className="text-xs text-red-600">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-[#374049]"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-lg bg-[#01767A] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#374049]"
            >
              Insert
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
